import React, { FC, useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import ReactNotification from 'react-notifications-component';
import { BrowserRouter } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'js-cookie';
import styled, { ThemeProvider } from 'styled-components';
import { decodeJwtToken } from 'utils/decodeJwtToken';
import { logData } from 'utils/helpers/logData.tsx';
import { Platform, usePlatform } from 'utils/hooks/usePlatform.tsx';
import { isProductionEnvironment } from 'utils/isProductionEnvironment.ts';

import { activeNotification } from './components/functions/activeNotification';
import { hasValidSubscription, UserProvider } from './store/User/Context';
import GlobalStyle from './styles/GlobalStyle';
import { theme } from './styles/theme';
import { userApiRequests } from './utils/api-requests/user';
import { ChatbotProvider } from './utils/context/ChatbotContext';
import { errorFallback } from './utils/errorFallback.tsx';
import { isAllowedDomain } from './utils/isAllowedDomain.ts';
import App from './App';

import 'react-notifications-component/dist/theme.css';

const Notification = styled.div`
    .notification-danger {
        background-color: #BA000B;
        border-left: none;
        box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
        border-radius: var(--box-border-radius);

        .timer-filler,
        .timer {
            background-color: #BA000B;
        }
    }

    .notification-warning {
        background-color: #ED811D;
        border-left: none;
        box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
        border-radius: var(--box-border-radius);

        .timer-filler,
        .timer {
            background-color: #ED811D;
        }
    }

    .notification-success {
        background-color: #0BAC25;
        border-left: none;
        box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
        border-radius: var(--box-border-radius);

        .timer-filler,
        .timer {
            background-color: #0BAC25;
        }
    }

    .notification-message {
        display: flex;
        color: #F2F5F9;

        span {
            margin-right: 10px;
        }
    }

    .notification-container-bottom-right {
        width: fit-content;
    }
`;

interface ApiActivityInfo {
    timestamp: string;
    method?: string;
    url?: string;
    data?: Record<string, string | number | boolean | null | undefined | object>;
    status?: number;
    type?: string;
}

export const axiosApiInstance = axios.create();

axiosApiInstance.defaults.baseURL = process.env.REACT_APP_API_URL;
axiosApiInstance.defaults.withCredentials = true;
axiosApiInstance.defaults.headers.post['Content-Type'] = 'application/json';

const SENSITIVE_FIELDS = [
    'password',
    'confirmPassword',
    'oldPassword',
    'refreshToken',
    'token',
    'accessToken'
];

const sanitizeData = (data: ApiActivityInfo['data']): ApiActivityInfo['data'] => {
    if (!data || typeof data !== 'object') {
        return data;
    }

    const sanitized = { ...data };

    for (const field of SENSITIVE_FIELDS) {
        if (field in sanitized) {
            sanitized[field] = '[REDACTED]';
        }
    }

    return sanitized;
};

const logApiActivity = (activityInfo: ApiActivityInfo) => {
    if (!isProductionEnvironment()) {
        return Promise.resolve();
    }

    return logData(activityInfo);
};

axiosApiInstance.interceptors.request.use(
    (config) => {
        if (config.url === '/user/log-error') {
            return config;
        }

        const timestamp = new Date().toISOString();

        const requestInfo = {
            timestamp,
            method: config.method,
            url: config.url,
            data: sanitizeData(config.data as ApiActivityInfo['data']),
            type: 'request'
        };

        logApiActivity(requestInfo);

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

let isRefreshingToken = false;

const Store: FC = () => {
    const platform = usePlatform();

    useEffect(() => {
        document.cookie = 'MonitorApiToken=; domain=skaner.investoro.pl; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
        document.cookie = 'MonitorApiRefresh=; domain=skaner.investoro.pl; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
        document.cookie = 'MonitorRoles=; domain=skaner.investoro.pl; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    }, []);

    useEffect(() => {
        const hideUrlBar = () => {
            if (platform === Platform.Android || platform === Platform.IOS) {
                window.scrollTo(0, 1);
            }
        };

        setTimeout(hideUrlBar, 100);

        window.addEventListener('scroll', hideUrlBar);
        window.addEventListener('touchstart', hideUrlBar);

        return () => {
            window.removeEventListener('scroll', hideUrlBar);
            window.removeEventListener('touchstart', hideUrlBar);
        };
    }, [platform]);

    useEffect(() => {
        const responseInterceptor = axiosApiInstance.interceptors.response.use((response) => {
            if (response.config.url === '/user/log-error') {
                return response;
            }

            const responseInfo = {
                timestamp: new Date().toISOString(),
                status: response.status,
                url: response.config.url,
                method: response.config.method,
                type: 'response'
            };

            logApiActivity(responseInfo);

            return response;
        }, async function (error) {
            const isRefreshed = error.request && error.request.responseURL && error.request.responseURL.includes('auth/refreshToken');
            if (isRefreshed) return Promise.reject(error);

            const originalRequest = error.config;

            if (error.response) {
                if (originalRequest.url === '/auth/authenticate') {
                    return Promise.reject(error);
                }

                if (error.response.status === 403 && error.response.data?.code?.includes('incorrect_password')) {
                    return Promise.reject(error);
                }

                if (error.response.status === 401 && (error.response.data && error.response.data.Message && error.response.data.Message.includes('Nieprawidłowe hasło'))) {
                    return Promise.reject(error);
                }

                if (error.response.status === 403 || error.response.status === 401) {
                    if (originalRequest._retry || isRefreshingToken) {
                        return Promise.reject(error);
                    }
                    isRefreshingToken = true;
                    let refreshToken = Cookies.get('MonitorApiRefresh');

                    if (!refreshToken && (platform === Platform.IOS || platform === Platform.Android)) {
                        refreshToken = localStorage.getItem('MonitorApiRefresh') || undefined;
                    }

                    if (refreshToken) {
                        try {
                            const refreshedUser = await userApiRequests.authenticateByToken(refreshToken);

                            if (refreshedUser && typeof refreshedUser === 'object') {
                                if (!isAllowedDomain(window.location.hostname)) {
                                    Cookies.set('MonitorApiToken', refreshedUser.token, { domain: '.investoro.pl' });
                                    Cookies.set('MonitorApiRefresh', refreshedUser.refreshToken, { domain: '.investoro.pl' });
                                }

                                if (isAllowedDomain(window.location.hostname)) {
                                    Cookies.set('MonitorApiToken', refreshedUser.token, { path: '/' });
                                    Cookies.set('MonitorApiRefresh', refreshedUser.refreshToken, { path: '/' });
                                }

                                if (platform === Platform.IOS || platform === Platform.Android) {
                                    localStorage.setItem('MonitorApiToken', refreshedUser.token);
                                    localStorage.setItem('MonitorApiRefresh', refreshedUser.refreshToken);

                                    if (refreshedUser.roles) {
                                        localStorage.setItem('MonitorRoles', refreshedUser.roles);
                                    }
                                }

                                axiosApiInstance.defaults.headers.common['Authorization'] = `Bearer ${refreshedUser.token}`;
                                isRefreshingToken = false;

                                const decodedToken = decodeJwtToken(refreshedUser.token);

                                if (!hasValidSubscription(decodedToken?.userRole || '')) {
                                    return Promise.reject(error);
                                }

                                originalRequest._retry = true;
                                originalRequest.headers['Authorization'] = `Bearer ${refreshedUser.token}`;

                                return axiosApiInstance(originalRequest);
                            } else {
                                isRefreshingToken = false;
                                clearAuthAndRedirect();
                            }
                        } catch {
                            isRefreshingToken = false;
                            clearAuthAndRedirect();
                        }
                    } else {
                        isRefreshingToken = false;
                        clearAuthAndRedirect();
                    }
                }
            }

            return Promise.reject(error);
        });

        return () => {
            axiosApiInstance.interceptors.response.eject(responseInterceptor);
        };
    }, [platform]);

    function clearAuthAndRedirect () {
        if (platform === Platform.IOS || platform === Platform.Android) {
            localStorage.removeItem('MonitorApiToken');
            localStorage.removeItem('MonitorApiRefresh');
            localStorage.removeItem('MonitorRoles');
        }

        const isProduction = isProductionEnvironment();

        if (isProduction) {
            Cookies.remove('MonitorApiToken');
            Cookies.remove('MonitorApiToken', { path: '', domain: '.investoro.pl' });
            Cookies.remove('MonitorApiRefresh');
            Cookies.remove('MonitorApiRefresh', { path: '', domain: '.investoro.pl' });
            Cookies.remove('MonitorRoles');
            Cookies.remove('MonitorRoles', { path: '', domain: '.investoro.pl' });
        } else {
            Cookies.remove('MonitorApiToken', { path: '/' });
            Cookies.remove('MonitorApiRefresh', { path: '/' });
            Cookies.remove('MonitorRoles', { path: '/' });
        }

        activeNotification(
            'Sesja wygasła',
            'Zaloguj się ponownie',
            'danger'
        );
        window.location.replace('/');
    }

    return (
        <BrowserRouter>
            <ThemeProvider theme={theme}>
                <GlobalStyle/>
                <Notification>
                    <ReactNotification/>
                </Notification>
                <UserProvider>
                    <ErrorBoundary FallbackComponent={errorFallback}>
                        <ChatbotProvider>
                            <App/>
                        </ChatbotProvider>
                    </ErrorBoundary>
                </UserProvider>
            </ThemeProvider>
        </BrowserRouter>
    );
};

export default Store;
